import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { MultiSelectDropdown } from "../../components/MultiSelectDropdown";
import type {
  LogReviewFilterKey,
  LogReviewMultiFilters,
} from "../../types/logReview";

export interface DrawerFilterFieldConfig {
  key: LogReviewFilterKey;
  label: string;
  options: readonly string[];
}

export interface LogReviewFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: readonly DrawerFilterFieldConfig[];
  applied: LogReviewMultiFilters;
  onApply: (next: LogReviewMultiFilters) => void;
}

/**
 * Side drawer holding the table's secondary facets (risk / team / status /
 * criticality). Edits a draft copy so a half-finished selection never
 * reaches the table — only Apply (or Clear and Close) commits.
 */
export const LogReviewFilterDrawer = ({
  open,
  onOpenChange,
  fields,
  applied,
  onApply,
}: LogReviewFilterDrawerProps) => {
  const [draft, setDraft] = useState<LogReviewMultiFilters>(applied);

  useEffect(() => {
    if (open) setDraft(applied);
  }, [open, applied]);

  const reset = () => setDraft({});

  const apply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const clearAndClose = () => {
    setDraft({});
    onApply({});
    onOpenChange(false);
  };

  const hasSelection = Object.values(draft).some((v) => (v?.length ?? 0) > 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-sm">
        <SheetHeader>
          <SheetTitle>Select Filters</SheetTitle>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={reset}
              className="text-sm font-semibold text-primary underline-offset-2 hover:underline"
            >
              Reset Filters
            </button>
          </div>
          {fields.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground">{field.label}</span>
              <MultiSelectDropdown
                options={field.options.map((o) => ({ value: o, label: o }))}
                selected={draft[field.key] ?? []}
                onChange={(next) => setDraft((d) => ({ ...d, [field.key]: next }))}
                placeholder={field.label}
                selectedNoun={field.label}
                className="w-full"
              />
            </div>
          ))}
        </div>

        <SheetFooter>
          <div className="flex w-full items-center justify-between">
            <Button variant="secondary" onClick={clearAndClose}>
              Clear and Close
            </Button>
            <Button onClick={apply} disabled={!hasSelection}>
              Apply Filters
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
